import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from "react-toastify";
import axios from 'axios';
import logo from '../../assets/images/logo.png';

const API_BASE = "https://api.mbsmartweb.com/api";

const Sidebar = ({ isOpen, setIsOpen }) => {
  const { darkMode } = useTheme();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    mobile: '',
    address: '',
    rankName: '',
  });
  const [loading, setLoading] = useState(true);
  const sidebarRef = useRef(null);
  const navigate = useNavigate();

  const menuItems = [
    // Main Navigation
    { path: '/dashboard', icon: 'fa-tachometer', label: 'Dashboard', section: 'main' },
    { path: '/profile', icon: 'fa-user', label: 'Profile', section: 'main' },
    { path: '/change-password', icon: 'fa-lock', label: 'Change Password', section: 'main' },
    
    // Funds Management - Deposit Submenu
    { 
      label: 'Deposit', 
      icon: 'fa-credit-card',
      submenu: true,
      section: 'funds',
      children: [
        { path: '/add-funds', icon: 'fa-dollar', label: 'Deposit USDT' },
      ]
    },
    
    // Withdrawal
     { path: '/deposit-history', icon: 'fa-history', label: 'Deposit History', section: 'funds' },
    { path: '/withdrawal', icon: 'fa-dollar', label: 'Withdrawal', section: 'funds' },
    { path: '/withdrawal-history', icon: 'fa-history', label: 'Withdrawal History', section: 'funds' },
    { path: '/main-wallet-history', icon: 'fa-history', label: 'Main Wallet History', section: 'funds' },
    { path: '/deposit-wallet-history', icon: 'fa-history', label: 'Deposit Wallet History', section: 'funds' },
   
    // Add to menuItems
    { path: '/broker-settings', icon: 'fa-server', label: 'Pair Account', section: 'main' },
    // Trading
    { path: '/activate-id', icon: 'fa-rocket', label: 'Activate ID', section: 'trading' },
    { path: '/bot-history', icon: 'fa-history', label: 'Bot History', section: 'trading' },
    { path: '/bot-activation-history', icon: 'fa-history', label: 'Bot Activation History', section: 'trading' },
     // Fuel Wallet (Submenu)
    { 
      label: 'Fuel Management', 
      icon: 'fa-gas-pump',
      submenu: true,
      section: 'fuel',
      children: [
        { path: '/add-funds2', icon: 'fa-dollar', label: 'Deposit Fuel' },
        { path: '/fuel-wallet-history', icon: 'fa-history', label: 'Fuel History' },
      ]
    },
    // Team Management (Submenu)
    { 
      label: 'Team Management', 
      icon: 'fa-users',
      submenu: true,
      section: 'team',
      children: [
        { path: '/my-direct-team', icon: 'fa-user-friends', label: 'My Direct Team' },
        { path: '/all-team', icon: 'fa-tree', label: 'All Team' },
        { path: '/level-tree', icon: 'fa-sitemap', label: 'Level Tree' },
      ]
    },
    
    // Income Report Submenu
    { 
      label: 'Income Report', 
      icon: 'fa-chart-line',
      submenu: true,
      section: 'reports',
      children: [
        { path: '/income-report/Trading-Profit', icon: 'fa-level-up', label: 'Trading Profit' },
        { path: '/income-report/Direct-Joining-Income', icon: 'fa-level-up', label: 'Direct Joining Income' },
        { path: '/income-report/Global-Joining-Royalty-Pool', icon: 'fa-level-down', label: 'Global Joining Royalty Pool' },
        { path: '/income-report/Team-Trading-Profit', icon: 'fa-users', label: 'Team Trading Profit' },
        { path: '/income-report/Global-Trading-Pool', icon: 'fa-diamond', label: 'Global Trading Pool' },
        { path: '/income-report/Leadership-Royalty-Pool', icon: 'fa-trophy', label: 'Leadership Royalty Pool' },
        { path: '/income-report/Rewards', icon: 'fa-gift', label: 'Rewards' },
      ]
    },
    
    // Support
    { path: '/support', icon: 'fa-headset', label: 'Support Chat', section: 'support' },
  ];

  // Fetch user details from API
  const fetchUserDetails = async () => {
    const userId = localStorage.getItem("UserId");
    
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/userdetails`, { userId });
      
      if (response.data) {
        setUserData({
          name: response.data.name || userId,
          email: response.data.email || '',
          mobile: response.data.mobile || '',
          address: response.data.Address || '',
          rankName: response.data.RankName || '',
        });
      }
    } catch (err) {
      console.error("Error fetching user details:", err);
      // Fallback to localStorage name if API fails
      const localName = localStorage.getItem("UserName");
      if (localName) {
        setUserData(prev => ({ ...prev, name: localName }));
      }
    } finally {
      setLoading(false);
    }
  };

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && sidebarRef.current && !sidebarRef.current.contains(event.target) && !event.target.closest('.menu-toggle')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, setIsOpen]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  // Fetch user details when component mounts
  useEffect(() => {
    fetchUserDetails();
  }, []);

  const toggleSubmenu = (label) => {
    setExpandedMenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const closeSidebar = () => {
    setIsOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("UserId");
    localStorage.removeItem("UserName");
    localStorage.removeItem("token");
    localStorage.removeItem("userData");
    toast.info("Logged out successfully");
    navigate("/login");
    closeSidebar();
  };

  // Get user data from localStorage as fallback
  const userId = localStorage.getItem("UserId") || "Guest";
  const displayName = userData.name || localStorage.getItem("UserName") || userId;

  return (
    <>
      {/* Overlay */}
      {isOpen && <div className="sidebar-overlay active" onClick={closeSidebar}></div>}
      
      <aside 
        ref={sidebarRef}
        className={`dashboard-sidebar ${isOpen ? 'open' : ''}`}
      >

        <div className="user-info-sidebar">
          <button className="sidebar-close" onClick={closeSidebar}>
            <i className="fa fa-times"></i>
          </button>
          <img 
            src={logo} 
            alt="User" 
          />
          <div>
            {loading ? (
              <>
                <h4>Loading...</h4>
                <p>ID: {userId}</p>
              </>
            ) : (
              <>
                <h4>{displayName}</h4>
                <p>ID: {userId}</p>
               <p className="rank-name" style={{ marginTop: '5px' }}>
                <i className="fas fa-trophy"></i> {userData.rankName}
              </p>
              </>
            )}
          </div>
        </div>

        <nav className="sidebar-nav">
          {/* MAIN NAVIGATION */}
          <div className="nav-section">
            <h4>MAIN NAVIGATION</h4>
            {menuItems.filter(item => item.section === 'main').map((item, index) => (
              <NavLink 
                key={index} 
                to={item.path} 
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={closeSidebar}
              >
                <i className={`fa ${item.icon}`}></i>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>

          {/* FUNDS MANAGEMENT */}
          <div className="nav-section">
            <h4>FUNDS MANAGEMENT</h4>
            {/* Deposit Submenu */}
            {menuItems.filter(item => item.submenu && item.section === 'funds').map((item, idx) => (
              <div key={idx} className="nav-submenu">
                <div 
                  className={`nav-link ${expandedMenus[item.label] ? 'expanded' : ''}`}
                  onClick={() => toggleSubmenu(item.label)}
                >
                  <i className={`fa ${item.icon}`}></i>
                  <span>{item.label}</span>
                  <i className={`fa fa-chevron-${expandedMenus[item.label] ? 'up' : 'down'} submenu-arrow`}></i>
                </div>
                <div className={`submenu-items ${expandedMenus[item.label] ? 'open' : ''}`}>
                  {item.children.map((child, childIdx) => (
                    <NavLink 
                      key={childIdx} 
                      to={child.path} 
                      className={({ isActive }) => `subnav-link ${isActive ? 'active' : ''}`}
                      onClick={closeSidebar}
                    >
                      <i className={`fa ${child.icon}`}></i>
                      <span>{child.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
            {menuItems.filter(item => item.section === 'funds' && !item.submenu).map((item, index) => (
              <NavLink 
                key={index} 
                to={item.path} 
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={closeSidebar}
              >
                <i className={`fa ${item.icon}`}></i>
                <span>{item.label}</span>
              </NavLink>
            ))}
            
          </div>

          {/* TRADING */}
          <div className="nav-section">
            <h4>Activation</h4>
            {menuItems.filter(item => item.section === 'trading').map((item, index) => (
              <NavLink 
                key={index} 
                to={item.path} 
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={closeSidebar}
              >
                <i className={`fa ${item.icon}`}></i>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>

            {/* FUEL MANAGEMENT */}
              <div className="nav-section">
            <h4>FUEL MANAGEMENT</h4>
            {menuItems.filter(item => item.submenu && item.section === 'fuel').map((item, idx) => (
              <div key={idx} className="nav-submenu">
                <div 
                  className={`nav-link ${expandedMenus[item.label] ? 'expanded' : ''}`}
                  onClick={() => toggleSubmenu(item.label)}
                >
                  <i className={`fa ${item.icon}`}></i>
                  <span>{item.label}</span>
                  <i className={`fa fa-chevron-${expandedMenus[item.label] ? 'up' : 'down'} submenu-arrow`}></i>
                </div>
                <div className={`submenu-items ${expandedMenus[item.label] ? 'open' : ''}`}>
                  {item.children.map((child, childIdx) => (
                    <NavLink 
                      key={childIdx} 
                      to={child.path} 
                      className={({ isActive }) => `subnav-link ${isActive ? 'active' : ''}`}
                      onClick={closeSidebar}
                    >
                      <i className={`fa ${child.icon}`}></i>
                      <span>{child.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* TEAM MANAGEMENT */}
          <div className="nav-section">
            <h4>TEAM MANAGEMENT</h4>
            {menuItems.filter(item => item.submenu && item.section === 'team').map((item, idx) => (
              <div key={idx} className="nav-submenu">
                <div 
                  className={`nav-link ${expandedMenus[item.label] ? 'expanded' : ''}`}
                  onClick={() => toggleSubmenu(item.label)}
                >
                  <i className={`fa ${item.icon}`}></i>
                  <span>{item.label}</span>
                  <i className={`fa fa-chevron-${expandedMenus[item.label] ? 'up' : 'down'} submenu-arrow`}></i>
                </div>
                <div className={`submenu-items ${expandedMenus[item.label] ? 'open' : ''}`}>
                  {item.children.map((child, childIdx) => (
                    <NavLink 
                      key={childIdx} 
                      to={child.path} 
                      className={({ isActive }) => `subnav-link ${isActive ? 'active' : ''}`}
                      onClick={closeSidebar}
                    >
                      <i className={`fa ${child.icon}`}></i>
                      <span>{child.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* REPORTS - Income Report Submenu */}
          <div className="nav-section">
            <h4>REPORTS</h4>
            {menuItems.filter(item => item.submenu && item.section === 'reports').map((item, idx) => (
              <div key={idx} className="nav-submenu">
                <div 
                  className={`nav-link ${expandedMenus[item.label] ? 'expanded' : ''}`}
                  onClick={() => toggleSubmenu(item.label)}
                >
                  <i className={`fa ${item.icon}`}></i>
                  <span>{item.label}</span>
                  <i className={`fa fa-chevron-${expandedMenus[item.label] ? 'up' : 'down'} submenu-arrow`}></i>
                </div>
                <div className={`submenu-items ${expandedMenus[item.label] ? 'open' : ''}`}>
                  {item.children.map((child, childIdx) => (
                    <NavLink 
                      key={childIdx} 
                      to={child.path} 
                      className={({ isActive }) => `subnav-link ${isActive ? 'active' : ''}`}
                      onClick={closeSidebar}
                    >
                      <i className={`fa ${child.icon}`}></i>
                      <span>{child.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* SUPPORT */}
          <div className="nav-section">
            <h4>SUPPORT</h4>
            {menuItems.filter(item => item.section === 'support').map((item, index) => (
              <NavLink 
                key={index} 
                to={item.path} 
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={closeSidebar}
              >
                <i className={`fa ${item.icon}`}></i>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>

          {/* Logout Button */}
          <div className="nav-section">
            <div className="logout-sidebar" onClick={handleLogout}>
              <i className="fa fa-sign-out"></i>
              <span>Logout</span>
            </div>
          </div>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;